/* ------------------------------------------------------------------------------------------------------------------
   EVO collection filter – "Kategorie" / "Ziel" groups in the facet sidebar and drawer (snippets/evo-filter-group).

   The values are the collections of the header shop menus; a card matches when its product is in that collection
   (handle → keys map rendered by sections/main-collection). Filtering runs on the rendered cards, without a request.
   The selection lives in the URL (?kategorie=…&ziel=…): the inputs sit inside the native facet form, so price,
   availability and sort requests carry it along, and after the theme's AJAX re-render the new elements re-apply it.
   Within a group values are OR-ed, the two groups are AND-ed.
   ------------------------------------------------------------------------------------------------------------------ */

(() => {
  if (window.customElements.get('evo-filter-groups')) return;

  const PARAMS = { k: 'kategorie', z: 'ziel' };
  const GROUPS = Object.keys(PARAMS);
  const pending = new Set();

  const getSection = (element) => element && element.closest('.shopify-section');

  const matchesGroup = (keys, group, selection) =>
    selection[group].length === 0 || selection[group].some((value) => keys.includes(`${group}:${value}`));

  function readSelection(inputs) {
    const params = new URLSearchParams(window.location.search);

    // values that have no input (stale link, own collection) would only produce an empty grid – ignore them
    return Object.fromEntries(
      GROUPS.map((group) => [
        group,
        [...new Set(params.getAll(PARAMS[group]))].filter((value) =>
          inputs.some((input) => input.dataset.evoFilter === group && input.value === value)
        ),
      ])
    );
  }

  function writeSelection(section, change) {
    const url = new URL(window.location.href);

    GROUPS.forEach((group) => {
      const param = PARAMS[group];
      const values = new Set(change === 'reset' ? [] : url.searchParams.getAll(param));

      if (change && change.group === group) {
        change.checked ? values.add(change.value) : values.delete(change.value);
      }

      url.searchParams.delete(param);
      values.forEach((value) => url.searchParams.append(param, value));
    });

    url.searchParams.delete('page');
    window.history.replaceState(window.history.state, '', url.toString());
    sync(section);
  }

  function formatTotal(element, count) {
    const template = count === 1 ? element.dataset.one : element.dataset.other;
    if (template) element.textContent = template.replace('{count}', count);
  }

  function renderChip(chip) {
    const wrapper = document.createElement('div');
    wrapper.className = 'removable-facet';
    wrapper.textContent = chip.label;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tap-area';
    button.dataset.evoChipGroup = chip.group;
    button.dataset.evoChipValue = chip.value;
    button.setAttribute('aria-label', `Filter „${chip.label}“ entfernen`);
    button.innerHTML =
      '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1.5 1.5l9 9m0-9l-9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

    wrapper.appendChild(button);
    return wrapper;
  }

  function sync(section) {
    const mapElement = section && section.querySelector('script[data-evo-filter-map]');
    if (!mapElement) return;

    let map;
    try {
      map = JSON.parse(mapElement.textContent);
    } catch (error) {
      return;
    }

    const inputs = [...section.querySelectorAll('input[data-evo-filter]')];
    const selection = readSelection(inputs);
    const active = GROUPS.some((group) => selection[group].length > 0);
    const entries = [...section.querySelectorAll('.product-list product-card')].map((card) => ({
      card,
      keys: map[card.getAttribute('handle')] || [],
    }));

    // drawer and sidebar both mirror the URL
    inputs.forEach((input) => {
      input.checked = selection[input.dataset.evoFilter].includes(input.value);
    });

    let visible = 0;
    entries.forEach(({ card, keys }) => {
      const show = GROUPS.every((group) => matchesGroup(keys, group, selection));
      card.classList.toggle('evo-filtered-out', !show);
      if (show) visible++;
    });

    // promo tiles only belong in the unfiltered grid
    section.querySelectorAll('.product-list > :not(product-card)').forEach((tile) => {
      tile.classList.toggle('evo-filtered-out', active);
    });

    // a value's count respects the selection of the other group, like Shopify's own facet counts
    inputs.forEach((input) => {
      const group = input.dataset.evoFilter;
      const key = `${group}:${input.value}`;
      const count = entries.filter(
        ({ keys }) =>
          keys.includes(key) && GROUPS.every((other) => other === group || matchesGroup(keys, other, selection))
      ).length;

      const countElement = input.parentElement.querySelector('[data-evo-count]');
      if (countElement) countElement.textContent = `(${count})`;
      input.disabled = count === 0 && !input.checked;
    });

    section.querySelectorAll('[data-evo-total]').forEach((element) => formatTotal(element, visible));

    const empty = section.querySelector('[data-evo-empty]');
    if (empty) empty.hidden = visible > 0;

    const chips = [];
    GROUPS.forEach((group) => {
      selection[group].forEach((value) => {
        const input = inputs.find((item) => item.dataset.evoFilter === group && item.value === value);
        chips.push({ group, value, label: input.dataset.label });
      });
    });

    section.querySelectorAll('[data-evo-chips]').forEach((container) => {
      container.replaceChildren(...chips.map(renderChip));
      container.hidden = chips.length === 0;
    });

    section.classList.toggle('evo-filter-active', active);
  }

  function schedule(section) {
    if (!section || pending.has(section)) return;
    pending.add(section);

    queueMicrotask(() => {
      pending.delete(section);
      sync(section);
    });
  }

  class EvoFilterGroups extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this);
      schedule(getSection(this));
    }

    disconnectedCallback() {
      this.removeEventListener('change', this);
    }

    handleEvent(event) {
      const input = event.target.closest('input[data-evo-filter]');
      if (!input) return;

      // keep it away from the native facet form: no section reload needed
      event.stopPropagation();
      writeSelection(getSection(this), { group: input.dataset.evoFilter, value: input.value, checked: input.checked });
    }
  }

  window.customElements.define('evo-filter-groups', EvoFilterGroups);

  // Price sorting: Shopify orders by the cheapest variant (a 4,90 € sample puts a 54,90 € tin before a 4,90 € pill box),
  // the cards show the featured variant. <evo-price-sort> (sections/main-collection) carries handle → card price and
  // re-orders the cards by it; equal prices keep Shopify's order. Promo tiles leave the grid while sorted by price.
  class EvoPriceSort extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.apply());
    }

    apply() {
      const list = getSection(this)?.querySelector('.product-list');
      if (!list) return;

      let prices;
      try {
        prices = JSON.parse(this.querySelector('script').textContent);
      } catch (error) {
        return;
      }

      const direction = this.dataset.direction === 'desc' ? -1 : 1;
      const cards = [...list.querySelectorAll(':scope > product-card')].map((card, index) => ({
        card,
        index,
        price: prices[card.getAttribute('handle')] ?? Number.MAX_SAFE_INTEGER,
      }));

      cards.sort((a, b) => (a.price - b.price) * direction || a.index - b.index);
      cards.forEach(({ card }) => list.appendChild(card));
      list.querySelectorAll(':scope > :not(product-card)').forEach((tile) => tile.classList.add('evo-sort-hidden'));
    }
  }

  window.customElements.define('evo-price-sort', EvoPriceSort);

  document.addEventListener('click', (event) => {
    const chipButton = event.target.closest('[data-evo-chip-group]');
    if (chipButton) {
      writeSelection(getSection(chipButton), {
        group: chipButton.dataset.evoChipGroup,
        value: chipButton.dataset.evoChipValue,
        checked: false,
      });
      return;
    }

    const resetButton = event.target.closest('[data-evo-reset]');
    if (resetButton) writeSelection(getSection(resetButton), 'reset');
  });

  // Native facet links (remove a price/availability chip) come with server URLs that don't know our parameters –
  // carry the current selection over before the theme's handler reads the href. "Clear all" resets everything.
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('a[is="facet-link"]');
      if (!link || link.classList.contains('facet-clear-all')) return;

      const section = getSection(link);
      if (!section || !section.querySelector('script[data-evo-filter-map]')) return;

      const current = new URLSearchParams(window.location.search);
      const url = new URL(link.href, window.location.origin);

      Object.values(PARAMS).forEach((param) => {
        url.searchParams.delete(param);
        current.getAll(param).forEach((value) => url.searchParams.append(param, value));
      });

      link.href = url.toString();
    },
    true
  );
})();

/* "Sortieren nach: …" above the grid of sort-only collections on mobile: the native select opens the phone's picker,
   a choice reloads the collection with the new order (page 1). */
if (!window.customElements.get('evo-sort-bar')) {
  window.customElements.define(
    'evo-sort-bar',
    class extends HTMLElement {
      connectedCallback() {
        const select = this.querySelector('select');
        if (!select || this._ready) return;
        this._ready = true;

        select.addEventListener('change', () => {
          const value = this.querySelector('[data-evo-sort-value]');
          if (value) value.textContent = select.options[select.selectedIndex].text;

          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', select.value);
          url.searchParams.delete('page');
          window.location.assign(url.toString());
        });
      }
    }
  );
}
